// The inbox (from almuzn-tracker app-whatsapp.ts, V15.27): Unassigned / Mine / All, a chat with its messages, reply,
// send a file, assign, mark read, reactions, delete for the team (admins), files kept in wa-inbox, voice notes as MP3,
// the AI agent's suggestion for the chat. Live through Supabase realtime, with a 60-second poll as a fallback.
// Returns a clean-up function (the shell calls it when another page opens).
import { callFn, rpc, sb } from "../sb.ts";
import type { Brand } from "../brand.ts";
import { t } from "../i18n.ts";
import { fmtTime, h, mount, toast } from "../ui.ts";
import { fill, varCount } from "../../supabase/functions/_shared/broadcast.ts";

interface Chat { line: string; wa_id: string; last_at: string; last_body: string | null; last_direction: string; last_in_at: string | null;
  unread: number; contact_id: string | null; assigned_to: string | null }
interface Msg { id: number; line: string; wa_id: string; direction: "in" | "out"; kind: string; body: string | null; media_id: string | null;
  media_mime: string | null; media_name: string | null; media_meta: { mp3?: string; dur?: number; kept?: string } | null; status: string;
  error: string | null; sent_by: string | null; created_at: string; reply_to: number | null; reactions: { by?: Record<string, string>; client?: string } }
interface Draft { id: number; body: string; agent: string | null; created_at: string }
type Tab = "unassigned" | "mine" | "all";
const WINDOW_MS = 24 * 3600e3;
const TICK: Record<string, string> = { queued: "🕓", sent: "✓", delivered: "✓✓", read: "✓✓", failed: "⚠" };
const FILE_TYPES = "image/jpeg,image/png,application/pdf,audio/ogg,audio/mpeg,audio/mp4,video/mp4";
const FILE_MAX = 16 * 1024 * 1024;

export async function inbox(view: HTMLElement, _brand: Brand, role: string): Promise<() => void> {
  const { data: { user } } = await sb.auth.getUser();
  const me = String(user?.email ?? "").toLowerCase();
  const canWrite = role === "admin" || role === "agent";
  const [{ data: settings }, { data: ai }, { data: tplRows }] = await Promise.all([
    sb.from("settings").select("quick_replies").maybeSingle(),
    sb.from("ai_settings").select("mode, agent_name").maybeSingle(),
    sb.from("wa_templates").select("id, name, provider_ref, lang, body").eq("status", "approved").neq("category", "authentication").order("name"),
  ]);
  // approved templates: the only thing WhatsApp lets us send once the 24-hour window is closed
  const tpls: { id: number; name: string; provider_ref: string; lang: string; body: string }[] = (tplRows ?? []).filter((x: any) => x.provider_ref);
  const quick: string[] = settings?.quick_replies ?? [];
  const aiOn = !!ai && ai.mode !== "off";
  let tab: Tab = (() => { try { return (localStorage.getItem("tab") as Tab) || "all"; } catch { return "all"; } })();
  let chats: Chat[] = [];
  const names = new Map<string, string>();
  let open: { line: string; wa_id: string } | null = null;
  let replyTo: Msg | null = null;
  let draftText: string | null = null;   // a suggestion put in the box

  const list = h("ul", { class: "chats" });
  const pane = h("section", { class: "thread" }, h("p", { class: "empty" }, t("Pick a chat")));
  const tabs = h("nav", { class: "tabs" });
  const search = h("input", { type: "search", placeholder: t("Search name or number"), oninput: () => renderList() });
  const wrap = h("div", { class: "inbox" }, h("aside", { class: "side" }, tabs, h("div", { class: "search" }, search), list), pane);
  mount(view, wrap);

  const label = (c: { wa_id: string; contact_id: string | null }) => (c.contact_id && names.get(c.contact_id)) || "+" + c.wa_id;

  async function loadChats() {
    const { data, error } = await sb.from("v_wa_chats").select("*").order("last_at", { ascending: false }).limit(300);
    if (error) return toast(error.message, true);
    chats = data as Chat[];
    const ids = [...new Set(chats.map((c) => c.contact_id).filter(Boolean))] as string[];
    if (ids.length) {
      const { data: cs } = await sb.from("contacts").select("id, name").in("id", ids);
      for (const c of cs ?? []) names.set(c.id, c.name);
    }
    renderList();
  }

  function renderList() {
    const counts = { unassigned: chats.filter((c) => !c.assigned_to).length, mine: chats.filter((c) => c.assigned_to === me).length, all: chats.length };
    const word: Record<Tab, string> = { unassigned: t("Unassigned"), mine: t("Mine"), all: t("All") };
    mount(tabs, (["unassigned", "mine", "all"] as Tab[]).map((x) => h("button", { class: x === tab ? "on" : "",
      onclick: () => { tab = x; try { localStorage.setItem("tab", x); } catch { /* private mode */ } renderList(); } }, `${word[x]} ${counts[x]}`)));
    const q = search.value.trim().toLowerCase(), qd = q.replace(/\D/g, "");
    const shown = chats.filter((c) => (tab === "all" || (tab === "mine" ? c.assigned_to === me : !c.assigned_to))
      && (!q || label(c).toLowerCase().includes(q) || (qd.length >= 3 && c.wa_id.includes(qd))));
    mount(list, shown.length ? shown.map((c) => h("li", { class: open?.line === c.line && open?.wa_id === c.wa_id ? "on" : "",
      onclick: () => openChat(c.line, c.wa_id) },
      h("div", { class: "who" }, h("b", { dir: "auto" }, label(c)), h("time", {}, fmtTime(c.last_at))),
      h("div", { class: "last" }, h("span", { dir: "auto" }, (c.last_direction === "out" ? t("You: ") : "") + (c.last_body ?? "")),
        c.unread > 0 && h("i", { class: "badge" }, String(c.unread))),
      c.assigned_to && h("small", { class: "muted" }, "→ " + c.assigned_to))) : h("li", { class: "empty" }, t("No chats here")));
  }

  function storageLink(ref: string | null | undefined): Promise<string | null> {
    if (!ref) return Promise.resolve(null);
    if (ref.startsWith("wa-inbox/")) return sb.storage.from("wa-inbox").createSignedUrl(ref.slice(9), 3600).then(({ data }) => data?.signedUrl ?? null);
    if (ref.startsWith("wa-docs/")) return Promise.resolve(sb.storage.from("wa-docs").getPublicUrl(ref.slice(8)).data.publicUrl);
    return Promise.resolve(null);   // a provider link (copy failed): needs the provider's keys, not shown in the browser
  }

  async function openChat(line: string, wa_id: string) {
    open = { line, wa_id };
    replyTo = null;
    draftText = null;
    renderList();
    await renderThread();
    if (canWrite && chats.find((c) => c.line === line && c.wa_id === wa_id)?.unread) {
      await rpc("mark_chat_read", { p_line: line, p_wa_id: wa_id }).catch(() => {});
      loadChats();
    }
  }

  async function renderThread() {
    if (!open) return;
    const { line, wa_id } = open;
    const [{ data, error }, { data: drafts }] = await Promise.all([
      sb.from("wa_messages").select("*").eq("line", line).eq("wa_id", wa_id).order("created_at", { ascending: true }).limit(500),
      canWrite ? sb.from("wa_agent_drafts").select("id, body, agent, created_at").eq("line", line).eq("wa_id", wa_id).eq("status", "new")
        .order("created_at", { ascending: false }).limit(1) : Promise.resolve({ data: [] as Draft[] }),
    ]);
    if (error) return toast(error.message, true);
    if (!open || open.line !== line || open.wa_id !== wa_id) return;   // another chat was opened meanwhile
    const msgs = data as Msg[];
    const draft = (drafts as Draft[] | null)?.[0] ?? null;
    const byId = new Map(msgs.map((m) => [m.id, m]));
    const chat = chats.find((c) => c.line === line && c.wa_id === wa_id);
    const lastIn = [...msgs].reverse().find((m) => m.direction === "in");
    const windowOpen = !!lastIn && Date.now() - Date.parse(lastIn.created_at) < WINDOW_MS;

    const assign = h("select", { disabled: !canWrite, onchange: async () => {
      try { await rpc("assign_chat", { p_line: line, p_wa_id: wa_id, p_email: assign.value }); toast(assign.value ? t("Assigned") : t("Unassigned")); loadChats(); }
      catch (e) { toast((e as Error).message, true); } } }, h("option", { value: "" }, t("Unassigned")));
    rpc<{ email: string; name: string }[]>("chat_staff", { p_line: line }).then((staff) => {
      for (const s of staff) assign.appendChild(h("option", { value: s.email, selected: s.email === chat?.assigned_to }, s.name));
    }).catch(() => {});

    const bubbles = await Promise.all(msgs.map(async (m) => {
      const [link, mp3] = await Promise.all([storageLink(m.media_id), storageLink(m.media_meta?.mp3)]);
      const quoted = m.reply_to ? byId.get(m.reply_to) : null;
      const reacts = [...Object.values(m.reactions?.by ?? {}), m.reactions?.client].filter(Boolean).join(" ");
      const isPdf = (m.media_mime ?? "").includes("pdf");
      return h("div", { class: `msg ${m.direction}${m.status === "failed" ? " failed" : ""}${m.sent_by === "ai" ? " ai" : ""}` },
        quoted && h("blockquote", { dir: "auto" }, (quoted.body ?? "").slice(0, 140)),
        link && m.kind === "image" && h("a", { href: link, target: "_blank", rel: "noopener" }, h("img", { src: link, alt: t("photo"), loading: "lazy" })),
        (mp3 || link) && m.kind === "audio" && h("div", { class: "voice" }, h("audio", { controls: true, preload: "none", src: mp3 ?? link! }),
          m.media_meta?.dur ? h("small", {}, `${Math.floor(m.media_meta.dur / 60)}:${String(Math.round(m.media_meta.dur % 60)).padStart(2, "0")}`) : null,
          !mp3 && h("a", { href: link!, target: "_blank", rel: "noopener", class: "muted" }, t("Download"))),
        link && m.kind === "video" && h("video", { controls: true, preload: "none", src: link }),
        link && (m.kind === "document" || (m.kind === "image" && isPdf)) && h("a", { href: link, target: "_blank", rel: "noopener" }, "📄 " + (m.media_name || t("Open file"))),
        h("p", { dir: "auto" }, link ? (m.body ?? "").replace(/^\[[^\]]*\]\s*/, "") : m.body ?? ""),
        h("footer", {}, h("time", {}, fmtTime(m.created_at)), m.direction === "out" && h("span", { class: "tick " + m.status, title: m.error ?? m.status }, TICK[m.status] ?? ""),
          m.sent_by && h("small", {}, m.sent_by === "ai" ? "🤖 " + (ai?.agent_name ?? "AI") : m.sent_by), reacts && h("span", { class: "reacts" }, reacts),
          canWrite && h("button", { class: "link", title: t("Reply"), onclick: () => { replyTo = m; renderCompose(); } }, "↩"),
          canWrite && h("button", { class: "link", title: t("React 👍"), onclick: async () => {
            try { await rpc("react_message", { p_message: m.id, p_emoji: "👍" }); renderThread(); } catch (e) { toast((e as Error).message, true); } } }, "👍"),
          role === "admin" && h("button", { class: "link", title: t("Delete for the team"), onclick: async () => {
            if (!confirm(t("Hide this message from everyone in the team? (It stays on the person's phone.)"))) return;
            try { await rpc("delete_message", { p_message: m.id }); renderThread(); loadChats(); } catch (e) { toast((e as Error).message, true); } } }, "🗑")),
        m.error && h("small", { class: "err" }, m.error));
    }));

    const compose = h("div", { class: "compose" });
    function renderCompose() {
      if (!canWrite) return mount(compose, h("p", { class: "muted" }, t("Viewers cannot send.")));
      const box = h("textarea", { rows: 2, dir: "auto", placeholder: windowOpen ? t("Message") : t("24-hour window closed: an approved template only"), disabled: !windowOpen });
      if (draftText != null) { box.value = draftText; draftText = null; }
      // window closed: pick an approved template and fill its {{n}} (typing an ID is the fallback when the list is empty)
      const tpl = h("input", { placeholder: t("Template Content SID (HX…)"), hidden: windowOpen || tpls.length > 0, spellcheck: false, dir: "ltr" });
      const pick = h("select", { hidden: windowOpen || !tpls.length }, h("option", { value: "" }, t("Choose a template…")),
        tpls.map((x) => h("option", { value: String(x.id) }, `${x.name} (${x.lang})`)));
      const varBox = h("div", {});
      const tplPreview = h("p", { class: "muted pre", dir: "auto" });
      let varInputs: HTMLInputElement[] = [];
      const chosen = () => tpls.find((x) => String(x.id) === pick.value);
      const drawPreview = () => { const c = chosen(); tplPreview.textContent = c ? fill(c.body, tplVars()) : ""; };
      const tplVars = () => Object.fromEntries(varInputs.map((el, i) => [String(i + 1), el.value.trim()]));
      pick.addEventListener("change", () => {
        const c = chosen();
        varInputs = Array.from({ length: c ? varCount(c.body) : 0 }, (_, i) => h("input", { placeholder: `{{${i + 1}}}`, dir: "auto", oninput: drawPreview }));
        mount(varBox, varInputs.length ? h("div", { class: "row" }, varInputs) : null);
        drawPreview();
      });
      const file = h("input", { type: "file", accept: FILE_TYPES, hidden: true });
      const picked = h("span", { class: "muted" });
      file.addEventListener("change", () => { picked.textContent = file.files?.[0] ? "📎 " + file.files[0].name : ""; });
      const sendBtn = h("button", { class: "primary", onclick: () => send() }, t("Send"));
      const send = async () => {
        const f = file.files?.[0];
        const c = !windowOpen && tpls.length ? chosen() : undefined;
        if (!windowOpen && tpls.length && (!c || varInputs.some((el) => !el.value.trim()))) return toast(t("Choose a template and fill every {{n}}"), true);
        if (windowOpen ? !box.value.trim() && !f : !c && !tpl.value.trim()) return;
        sendBtn.disabled = true;
        try {
          let body: Record<string, unknown> = windowOpen ? { text: box.value }
            : c ? { template: c.provider_ref, lang: c.lang, variables: tplVars(), preview: fill(c.body, tplVars()) } : { template: tpl.value.trim() };
          if (f && windowOpen) {
            if (f.size > FILE_MAX) return toast(t("16 MB at most"), true);
            const path = `out/${crypto.randomUUID()}/${f.name.replace(/[^\p{L}\p{N}._ -]/gu, "_").slice(-100) || "file"}`;
            const { error } = await sb.storage.from("wa-docs").upload(path, f, { contentType: f.type, upsert: false });
            if (error) return toast(error.message, true);
            body = { text: box.value, file: { path } };
          }
          const r = await callFn("wa-send", { mode: "reply", line, wa_id, reply_to: replyTo?.id, draft_id: draft?.id, ...body });
          if (!r.sent) toast((r.error ?? t("not sent")) + (r.hint ? " — " + r.hint : ""), true);
          replyTo = null;
          await renderThread();
          loadChats();
        } finally { sendBtn.disabled = false; }
      };
      box.addEventListener("keydown", (e) => { if (e.key === "Enter" && !e.shiftKey && !matchMedia("(pointer: coarse)").matches) { e.preventDefault(); send(); } });
      mount(compose,
        draft && h("div", { class: "draft" }, h("b", {}, "🤖 " + t("Suggestion from {name}", { name: draft.agent || "AI" })),
          h("p", { dir: "auto" }, draft.body),
          h("div", { class: "row end" },
            h("button", { onclick: async () => { await rpc("dismiss_ai_draft", { p_draft: draft.id }).catch(() => {}); renderThread(); } }, t("Dismiss")),
            h("button", { class: "primary", disabled: !windowOpen, onclick: () => { box.value = draft.body; box.focus(); } }, t("Use"))),
          !windowOpen && h("small", { class: "muted" }, t("24-hour window closed: an approved template only"))),
        replyTo && h("div", { class: "replying" }, "↩ " + (replyTo.body ?? "").slice(0, 80), h("button", { class: "link", onclick: () => { replyTo = null; renderCompose(); } }, "✕")),
        quick.length > 0 && windowOpen && h("select", { onchange: (e: Event) => { const s = e.target as HTMLSelectElement; box.value = s.value; s.selectedIndex = 0; box.focus(); } },
          h("option", { value: "" }, t("Quick replies…")), quick.map((q) => h("option", { value: q }, q.slice(0, 60)))),
        !windowOpen && tpls.length > 0 && h("div", {}, pick, varBox, tplPreview),
        h("div", { class: "row" }, box, tpl,
          windowOpen && h("button", { class: "icon", title: t("Send a file"), onclick: () => file.click() }, "📎"),
          aiOn && h("button", { class: "icon", title: t("Ask the AI for a suggestion"), onclick: async (e: Event) => {
            const b = e.currentTarget as HTMLButtonElement;
            b.disabled = true; b.textContent = "…";
            const r = await callFn("ai-agent", { mode: "suggest", line, wa_id });
            b.disabled = false; b.textContent = "✨";
            if (!r.ok) return toast(r.error ?? "AI", true);
            if (r.handover) toast(t("The AI thinks a person should answer: {why}", { why: r.handover_reason || "" }));
            renderThread();
          } }, "✨"),
          sendBtn),
        file, picked);
    }
    renderCompose();

    const scroller = h("div", { class: "msgs" }, bubbles);
    mount(pane,
      h("header", { class: "thread-head" }, h("button", { class: "link back", onclick: () => { open = null; mount(pane); wrap.classList.remove("has-open"); renderList(); } }, "←"),
        h("b", { dir: "auto" }, chat ? label(chat) : "+" + wa_id), h("span", { class: "muted", dir: "ltr" }, " +" + wa_id + " · via +" + line),
        canWrite && !chat?.contact_id && h("a", { class: "small", href: `?new=${wa_id}&line=${line}#contacts` }, "+ " + t("Contact")),
        h("span", { class: "grow" }), h("span", { class: windowOpen ? "pill ok" : "pill" }, windowOpen ? t("window open") : t("template only")), assign),
      scroller, compose);
    scroller.scrollTop = scroller.scrollHeight;
    wrap.classList.add("has-open");
  }

  // live: a new message, a tick or an AI suggestion refreshes the list (and the open chat when it is that one)
  let timer: number | undefined;
  const refresh = (row?: { line?: string; wa_id?: string }) => {
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      loadChats();
      if (open && (!row || (row.line === open.line && row.wa_id === open.wa_id))) renderThread();
    }, 300);
  };
  const channel = sb.channel("inbox-" + crypto.randomUUID())
    .on("postgres_changes", { event: "*", schema: "public", table: "wa_messages" }, (p) => refresh(p.new as any))
    .on("postgres_changes", { event: "*", schema: "public", table: "wa_agent_drafts" }, (p) => refresh(p.new as any))
    .subscribe();
  const poll = setInterval(() => refresh(), 60_000);
  await loadChats();
  const first = new URLSearchParams(location.search).get("wa");
  if (first) history.replaceState(null, "", location.pathname + location.hash);
  const hit = first && chats.find((c) => c.wa_id === first.replace(/\D/g, ""));
  if (hit) openChat(hit.line, hit.wa_id);
  return () => { clearInterval(poll); clearTimeout(timer); sb.removeChannel(channel); };
}
